/** Native lookbook hotspots; product data and links remain server rendered. */
let panelSerial = 0;
const activeByDocument = new WeakMap();
export class WalcomLookbook {
  constructor(root) {
    this.root = root;
    this.document = root.ownerDocument;
    this.abort = new AbortController();
    this.trigger = root.querySelector('[data-walcom-lookbook-trigger]');
    this.panel = root.querySelector('[data-walcom-lookbook-panel]');
    this.closeButton = root.querySelector('[data-close-lookbook-dialog-button]');
    this.expanded = false;
    if (!this.trigger || !this.panel) return;
    if (!this.panel.id) this.panel.id = `WalcomLookbook-${++panelSerial}`;
    this.dialog = this.panel.tagName === 'DIALOG';
    this.trigger.setAttribute('aria-controls', this.panel.id);
    this.trigger.setAttribute('aria-haspopup', this.dialog ? 'dialog' : 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    const options = { signal: this.abort.signal };
    this.trigger.addEventListener('click', () => this.expanded ? this.close(true) : this.open(), options);
    this.closeButton?.addEventListener('click', () => this.close(true), options);
    root.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.expanded) { event.preventDefault(); event.stopPropagation(); this.close(true); }
    }, options);
    this.panel.addEventListener('cancel', event => { event.preventDefault(); this.close(true); }, options);
    this.panel.addEventListener('close', () => this.close(true), options);
    this.panel.addEventListener('click', event => {
      if (!this.dialog || event.target !== this.panel) return;
      const box = this.panel.getBoundingClientRect?.();
      if (!box || event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close(true);
    }, options);
    this.document.addEventListener('pointerdown', event => {
      if (this.expanded && !root.contains(event.target)) this.close(false);
    }, options);
    this.document.defaultView?.addEventListener('resize', () => this.close(false), options);
  }
  open() {
    if (!this.panel || !this.trigger) return;
    const previous = activeByDocument.get(this.document);
    if (previous && previous !== this) previous.close(false);
    this.panel.hidden = false;
    this.expanded = true;
    this.root.classList.toggle('walcom-lookbook-open', true);
    this.trigger.setAttribute('aria-expanded', 'true');
    activeByDocument.set(this.document, this);
    if (this.dialog) {
      if (typeof this.panel.showModal === 'function') this.panel.showModal();
      else this.panel.setAttribute('open', '');
      this.panel.querySelector('a[href], button')?.focus();
    }
  }
  close(focus = false) {
    if (!this.expanded) return;
    this.expanded = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.root.classList.toggle('walcom-lookbook-open', false);
    if (activeByDocument.get(this.document) === this) activeByDocument.delete(this.document);
    if (this.dialog && this.panel.open && typeof this.panel.close === 'function') this.panel.close();
    this.panel.removeAttribute?.('open');
    this.panel.hidden = true;
    if (focus) this.trigger.focus();
  }
  disconnect() { this.close(false); this.abort.abort(); }
}

export class WalcomLookbookCollection {
  constructor(root) {
    this.root = root;
    this.abort = new AbortController();
    this.image = root.querySelector('[data-lookbook-image]');
    this.panel = root.querySelector('.collection-wrapper');
    this.show = root.querySelector('[data-show-btn]');
    this.hide = root.querySelector('[data-hide-btn]');
    const close = root.querySelector('[data-lookbook-show-all-close]');
    if (!this.image || !this.panel || !this.show) return;
    if (!this.panel.id) this.panel.id = `WalcomLookbookCollection-${++panelSerial}`;
    this.show.setAttribute('aria-controls', this.panel.id);
    const options = {signal: this.abort.signal};
    this.show.addEventListener('click', () => this.toggle(true), options);
    this.hide?.addEventListener('click', () => this.toggle(false, true), options);
    close?.addEventListener('click', () => this.toggle(false, true), options);
    root.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.expanded) { event.preventDefault(); this.toggle(false, true); }
    }, options);
    this.toggle(false);
  }
  toggle(expanded, focus = false) {
    if (!this.image || !this.panel || !this.show) return;
    this.expanded = expanded;
    this.image.classList.toggle('show-all', expanded);
    this.image.classList.toggle('show-all-products', expanded);
    this.panel.hidden = !expanded;
    this.panel.inert = !expanded;
    this.show.setAttribute('aria-expanded', String(expanded));
    if (expanded) this.panel.querySelector('a[href], button')?.focus();
    if (focus) this.show.focus();
  }
  disconnect() { this.abort.abort(); }
}

if (typeof document !== 'undefined') {
  const controllers = new WeakMap();
  const hotspot = '.walcom-legacy [data-walcom-lookbook-hotspot]';
  const collection = '.walcom-legacy lookbook-with-show-all';
  function init(scope) {
    for (const [selector, Controller] of [[hotspot, WalcomLookbook], [collection, WalcomLookbookCollection]]) {
      const roots = [...scope.querySelectorAll(selector)];
      if (scope.matches?.(selector)) roots.unshift(scope);
      roots.forEach(root => { if (!controllers.has(root)) controllers.set(root, new Controller(root)); });
    }
  }
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(`${hotspot}, ${collection}`).forEach(root => { controllers.get(root)?.disconnect(); controllers.delete(root); });
  });
}
